import { accessPaths, levelOf, machinesIn, reachCounts, ruleState, shownInConnectivity } from '@kronos/domain'
import type { AccessLevel, AccessPath, AccessRule, Machine, MachineGroup, Visibility } from '@kronos/domain'
import type { ConnectivityPage, TailnetDevice } from '../../data'

/**
 * The page's data, arranged the way the three tabs read it. No rules live
 * here — "who may reach what, and why" is `accessPaths` from the domain, the
 * same function the API builds the network policy from.
 */

export type NetState = 'online' | 'offline' | 'not_joined'

export interface MachineView extends Machine {
  readonly device: TailnetDevice | null
  readonly net: NetState
  readonly groupIds: readonly string[]
  readonly robot: ConnectivityPage['robots'][number] | null
  /** userId → why they can get in. */
  readonly access: ReadonlyMap<string, readonly AccessPath[]>
}

export interface PersonView {
  readonly id: string
  readonly name: string
  readonly email: string
  readonly initials: string
  readonly devices: readonly TailnetDevice[]
  readonly online: boolean
  readonly teamIds: readonly string[]
  readonly robots: number
  readonly computers: number
  /** Rules naming this person directly (not through a team). */
  readonly rules: readonly AccessRule[]
  readonly roles: readonly string[]
  /** The admin's choice; null: the default (engineers, admins, anyone on the network or with access). */
  readonly visibility: Visibility
}

export interface GroupView extends MachineGroup {
  readonly machineIds: readonly string[]
}

export interface TeamView {
  readonly id: string
  readonly name: string
  readonly memberIds: readonly string[]
}

export interface View {
  readonly page: ConnectivityPage
  readonly now: string
  readonly machines: readonly MachineView[]
  /** The people listed in Connectivity — see `shownInConnectivity`. */
  readonly people: readonly PersonView[]
  /** Everyone else with a Kronos account, for the "hidden" list. */
  readonly hidden: readonly PersonView[]
  readonly teams: readonly TeamView[]
  readonly groups: readonly GroupView[]
  /** Active rules, as stored. */
  readonly rules: readonly AccessRule[]
  /** Active rules that end, soonest first. */
  readonly ending: readonly AccessRule[]
  /** Tagged devices on the network that no machine here manages yet — R2 on the day Kronos takes over. */
  readonly unclaimed: readonly TailnetDevice[]
  /** People signed in to the network who have no Kronos account, with their devices. */
  readonly networkOnly: readonly { readonly email: string; readonly name: string; readonly devices: readonly TailnetDevice[] }[]
  readonly machine: (id: string) => MachineView | undefined
  readonly person: (id: string) => PersonView | undefined
  readonly team: (id: string) => TeamView | undefined
  readonly group: (id: string) => GroupView | undefined
}

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('')
}

export function derive(page: ConnectivityPage): View {
  const { world, now } = page
  const paths = accessPaths(world, now)
  const counts = reachCounts(world, now)
  const groups: GroupView[] = world.groups.map((g) => ({ ...g, machineIds: machinesIn(world, g) }))
  const groupsOf = new Map<string, string[]>()
  for (const g of groups) for (const id of g.machineIds) groupsOf.set(id, [...(groupsOf.get(id) ?? []), g.id])
  const robots = new Map(page.robots.map((r) => [r.id, r]))

  const machines: MachineView[] = world.machines
    .filter((m) => m.retiredAt === null)
    .map((m) => {
      const device = page.devices.find((d) => d.tags.includes(m.tag)) ?? null
      return {
        ...m,
        device,
        net: device === null ? 'not_joined' : device.online ? 'online' : 'offline',
        groupIds: groupsOf.get(m.id) ?? [],
        robot: m.robotId ? (robots.get(m.robotId) ?? null) : null,
        access: paths.get(m.id) ?? new Map(),
      }
    })

  const rules = world.rules.filter((r) => ruleState(r, now) === 'active')
  const ending = rules.filter((r) => r.expiresAt !== null).sort((a, b) => (a.expiresAt as string).localeCompare(b.expiresAt as string))
  const everyone: PersonView[] = page.people
    .filter((p) => p.active)
    .map((p) => {
      const devices = page.devices.filter((d) => d.ownerEmail === p.email.toLowerCase())
      const c = counts.get(p.id) ?? { robots: 0, computers: 0 }
      return {
        id: p.id,
        name: p.name,
        email: p.email,
        initials: initials(p.name),
        devices,
        online: devices.some((d) => d.online),
        teamIds: world.teamMembers.filter((t) => t.userId === p.id).map((t) => t.teamId),
        robots: c.robots,
        computers: c.computers,
        rules: rules.filter((r) => r.userId === p.id),
        roles: p.roles,
        visibility: p.visibility,
      }
    })
  const onNetwork = new Set(page.networkUsers.map((u) => u.email))
  const listed = (p: PersonView): boolean =>
    shownInConnectivity({
      roles: p.roles,
      onNetwork: p.devices.length > 0 || onNetwork.has(p.email.toLowerCase()),
      hasAccess: p.robots + p.computers > 0 || p.rules.length > 0 || p.teamIds.length > 0,
      choice: p.visibility,
    })
  const people = everyone.filter(listed)
  const hidden = everyone.filter((p) => !listed(p))

  const teams: TeamView[] = world.teams.map((t) => ({
    ...t,
    memberIds: world.teamMembers.filter((m) => m.teamId === t.id).map((m) => m.userId),
  }))

  const by = <T extends { id: string }>(xs: readonly T[]) => {
    const map = new Map(xs.map((x) => [x.id, x]))
    return (id: string) => map.get(id)
  }
  const managed = new Set(world.machines.filter((m) => m.retiredAt === null).map((m) => m.tag))
  const unclaimed = page.devices.filter((d) => d.tags.length > 0 && !d.tags.some((t) => managed.has(t)))
  const known = new Set(page.people.map((p) => p.email.toLowerCase()))
  const networkOnly = page.networkUsers
    .filter((u) => !known.has(u.email))
    .map((u) => ({ ...u, devices: page.devices.filter((d) => d.ownerEmail === u.email) }))
  return {
    page,
    now,
    machines,
    people,
    hidden,
    teams,
    groups,
    rules,
    ending,
    unclaimed,
    networkOnly,
    machine: by(machines),
    person: by(everyone),
    team: by(teams),
    group: by(groups),
  }
}

const shortDay = (iso: string): string => new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })

/** "Autonomy → Site A · dev", "Directly → this machine · until 7 Nov" — one reason, in words. */
export function describePath(view: View, path: AccessPath): string {
  const who = path.teamId ? (view.team(path.teamId)?.name ?? 'A team') : 'Directly'
  const what = path.groupId ? (view.group(path.groupId)?.name ?? 'a group') : 'this machine'
  const level = path.level === 'restricted' ? ' · restricted' : ''
  return `${who} → ${what}${level}${path.expiresAt ? ` · until ${shortDay(path.expiresAt)}` : ''}`
}

/** One reason, as a sentence ends: "their own access", "the Autonomy team, through Site A". */
export function reasonText(view: View, path: AccessPath): string {
  const team = path.teamId ? view.team(path.teamId)?.name : null
  const group = path.groupId ? view.group(path.groupId)?.name : null
  const base = team ? (group ? `the ${team} team, through ${group}` : `the ${team} team`) : group ? `their access to ${group}` : 'their own access'
  const level = path.level === 'restricted' ? ', restricted' : ''
  return `${base}${level}${path.expiresAt ? `, until ${shortDay(path.expiresAt)}` : ''}`
}

/** A rule's two ends, in words: "Vaishanth Ramaraj", "Office 3090". */
export function ruleWho(view: View, r: AccessRule): string {
  return r.teamId ? (view.team(r.teamId)?.name ?? 'a team') : (view.person(r.userId ?? '')?.name ?? 'someone')
}
export function ruleWhat(view: View, r: AccessRule): string {
  return r.groupId ? (view.group(r.groupId)?.name ?? 'a group') : (view.machine(r.machineId ?? '')?.name ?? 'a machine')
}

export type RowKind = 'people' | 'teams'
export type ColKind = 'machines' | 'groups'

/**
 * One square of the grid: the rule made exactly for this row and column (the
 * one a press turns on or off), and any access that reaches it some other way
 * — through a team the person is in, a group the machine is in. Inherited
 * access is shown, never toggled here: it is changed where it was given.
 */
export function cell(view: View, rowKind: RowKind, rowId: string, colKind: ColKind, colId: string): { exact: AccessRule | null; inherited: readonly string[] } {
  const exact =
    view.rules.find(
      (r) =>
        (rowKind === 'people' ? r.userId === rowId : r.teamId === rowId) && (colKind === 'machines' ? r.machineId === colId : r.groupId === colId),
    ) ?? null
  const inherited: string[] = []
  if (rowKind === 'people' && colKind === 'machines') {
    for (const p of view.machine(colId)?.access.get(rowId) ?? []) if (p.ruleId !== exact?.id) inherited.push(describePath(view, p))
  } else if (rowKind === 'people' && colKind === 'groups') {
    const teams = view.person(rowId)?.teamIds ?? []
    for (const r of view.rules) if (r.groupId === colId && r.teamId && teams.includes(r.teamId)) inherited.push(`via ${view.team(r.teamId)?.name}`)
  } else if (rowKind === 'teams' && colKind === 'machines') {
    const groups = view.machine(colId)?.groupIds ?? []
    for (const r of view.rules) if (r.teamId === rowId && r.groupId && groups.includes(r.groupId)) inherited.push(`via ${view.group(r.groupId)?.name}`)
  }
  return { exact, inherited }
}

export function accessLevel(paths: readonly AccessPath[]): AccessLevel {
  return levelOf(paths)
}

/** "200 robots · 6 PCs", or "Nothing". */
export function reachText(robots: number, computers: number): string {
  const parts: string[] = []
  if (robots) parts.push(`${robots} ${robots === 1 ? 'robot' : 'robots'}`)
  if (computers) parts.push(`${computers} ${computers === 1 ? 'PC' : 'PCs'}`)
  return parts.length ? parts.join(' · ') : 'Nothing'
}
